import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Card from "../Card";
import BalanceChart from "../BalanceChart";
import ProViewToggle from "../ProViewToggle";
import BuySellModal from "../BuySellModal";
import SwapModal from "../SwapModal";
import { WalletIcon, TransferIcon, SwapIcon } from "../../icons";
import { formatUsd } from "../../utils/format";
import TransferModal from "../TransferModal";
import GetStartedGuide from "../GetStartedGuide";
import { BalanceCardSkeleton } from "../../../components/Skeleton/BalanceCardSkeleton";

const actionButtonClass = `group inline-flex items-center gap-2 justify-center px-4 py-[9px] rounded-[8px] 
border border-primary-300 bg-background-surface text-sm font-medium text-primary-600 hover:border-primary-300 
hover:bg-primary-300 hover:text-white custom-transition`;

const actionButtonIconClass = "shrink-0 text-[#4AB932] group-hover:text-white";

// Scale down font size as string length increases
const getBalanceTextClass = (length) => {
  if (length > 17) return "text-xl sm:text-2xl";
  if (length > 14) return "text-2xl sm:text-[28px]";
  if (length > 11) return "text-3xl sm:text-[34px]";
  return "text-[40px]";
};

const BalanceCard = ({ proView: proViewProp, onProViewChange, loading, setLoadData }) => {
  const [internalProView, setInternalProView] = useState(false);
  const proView = proViewProp ?? internalProView;
  const setProView = onProViewChange ?? setInternalProView;

  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isBuySellModalOpen, setIsBuySellModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);

  // 1. Fetch live crypto accounts from Redux
  const cryptoAccounts = useSelector((state) => (state.client?.clientInfo?.crypto_accounts ?? []));

  // 2. Dynamically calculate total USD balance & total asset count
  const { totalBalanceUsd, assetCount, isNewUser } = useMemo(() => {
    if (!cryptoAccounts || cryptoAccounts.length === 0) {
      return { totalBalanceUsd: 0, assetCount: 0, isNewUser: true };
    }
    
    const isNewUser = !cryptoAccounts?.some((account) => account?.cryptoAddresses?.length > 0);
    const total = cryptoAccounts.reduce((sum, account) => {
      const balance = Number(account?.balance ?? 0);
      const unitPrice = Number(account?.unit_price_usd ?? 0);
      return sum + balance * unitPrice;
    }, 0);

    return {
      totalBalanceUsd: total,
      assetCount: cryptoAccounts.length,
      isNewUser,
    };
  }, [cryptoAccounts]);

  const formattedBalance = useMemo(() => formatUsd(totalBalanceUsd), [totalBalanceUsd]);
  const balanceTextClass = useMemo(() => getBalanceTextClass(formattedBalance.length), [formattedBalance]);

  if (loading) {
    return <BalanceCardSkeleton />;
  }

  return (
    <>
      <Card className="p-6 font-outfit">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-1 min-w-0 flex flex-col justify-between gap-6">
            <div>
              <div className="flex items-center justify-between">
                <p className="text-[12px] font-medium tracking-[0.08em] text-text-secondary uppercase">
                  Total crypto balance
                </p>
                <div className="sm:hidden">
                  <ProViewToggle enabled={proView} onChange={setProView} />
                </div>
              </div>

              {/* Dynamic Live Balance with auto-scaling font size */}
              <p className={`mt-1 font-semibold text-primary-300 tracking-tight break-all ${balanceTextClass}`}>
                {formattedBalance}
              </p>

              {/* Dynamic Asset Count */}
              <p className="mt-1 max-sm:hidden text-sm text-text-secondary">
                Across {assetCount} {assetCount === 1 ? "asset" : "assets"}
              </p>

              {/* <p className="text-[11px] text-primary-300 font-semibold">
                +$0 this week
              </p> */}

              <div className="mt-4 max-sm:hidden">
                <ProViewToggle enabled={proView} onChange={setProView} />
              </div>
            </div>

            {!isNewUser ? (
              <div className="sm:flex grid grid-cols-4 sm:flex-wrap gap-3">
                <button
                  type="button"
                  className={[
                    actionButtonClass,
                    "bg-primary-300 max-sm:col-span-4 text-white",
                  ].join(" ")}
                  onClick={() => setIsBuySellModalOpen(true)}
                >
                  <span
                    className={[actionButtonIconClass, "text-white"].join(" ")}
                  >
                    <WalletIcon className="w-[18px] h-[18px]" />
                  </span>
                  Buy &amp; Sell
                </button>
                <button
                  type="button"
                  className={[actionButtonClass, "max-sm:col-span-2"].join(" ")}
                  onClick={() => setIsTransferModalOpen(true)}
                >
                  <span className={actionButtonIconClass}>
                    <TransferIcon />
                  </span>
                  Transfer
                </button>
                <button
                  type="button"
                  className={[actionButtonClass, "max-sm:col-span-2"].join(" ")}
                  onClick={() => setIsSwapModalOpen(true)}
                >
                  <span className={actionButtonIconClass}>
                    <SwapIcon />
                  </span>
                  Swap
                </button>
              </div>
            ) : (
              <div className="sm:flex grid grid-cols-4 sm:flex-wrap gap-3">
                <button
                  type="button"
                  disabled={assetCount <= 0}
                  className={[
                    actionButtonClass,
                    "bg-primary-300 max-sm:col-span-4 text-white",
                    "disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none disabled:shadow-none"
                  ].join(" ")}
                  onClick={() => (assetCount > 0 ? setIsGuideOpen(true) : null)}
                >
                  <span
                    className={[actionButtonIconClass, "text-white"].join(" ")}
                  >
                    <WalletIcon className="w-[18px] h-[18px]" />
                  </span>
                  Get Started
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-between gap-6">
            <BalanceChart />
          </div>
        </div>
      </Card>

      <GetStartedGuide
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        onBuyCrypto={() => setIsBuySellModalOpen(true)}
        onDepositCrypto={() => setIsTransferModalOpen(true)}
      />

      <BuySellModal
        isOpen={isBuySellModalOpen}
        onClose={() => setIsBuySellModalOpen(false)}
        showBuyGuide={isNewUser}
        isNewUser={isNewUser}
        totalBalanceUsd={totalBalanceUsd}
        onGuideDepositCrypto={() => {
          setIsBuySellModalOpen(false);
          setIsTransferModalOpen(true);
        }}
        setLoadData={setLoadData}
      />
      
      <TransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        showDepositGuide={isNewUser}
        isNewUser={isNewUser}
        totalBalanceUsd={totalBalanceUsd}
        onGuideBuyCrypto={() => {
          setIsTransferModalOpen(false);
          setIsBuySellModalOpen(true);
        }}
        setLoadData={setLoadData}
      />
      
      <SwapModal
        isOpen={isSwapModalOpen}
        onClose={() => setIsSwapModalOpen(false)}
        setLoadData={setLoadData}
        cryptoAccounts={cryptoAccounts}
      />
    </>
  );
};

export default BalanceCard;